// src/components/planner/tabs/OverviewTab.tsx

import React, { useState, useEffect, useRef } from 'react';
import { Clock, Play, Pause, Maximize2, Target, Plus, Filter, Trash2, Edit2, Check, ChevronDown, AlertCircle, CalendarCheck, CalendarDays, Inbox, CheckCircle2, Layers, Folder, ChevronRight } from 'lucide-react';
import QuickStudyModal from '../modals/QuickStudyModal';
import GoalModal from '../modals/GoalModal';
import TaskModal from '../modals/TaskModal';
import StudyTypeModal from '../modals/StudyTypeModal';
import ManualSessionModal from '../modals/ManualSessionModal';
import { useStudyTimer } from '../../../contexts/StudyTimerContext';

const API_BASE_URL = '/api';

// ✅ activeTimer e setActiveTimer removidos da interface — vêm do contexto
interface OverviewTabProps {
    todayData: any;
    weekStats: any;
    goals: any[];
    studyHistory: any[];
    reviewContents: any[];
    tasks: any[];
    taskGroups: any[];
    subjects: any[];
    onReload: () => void;
    onReloadTasks: () => void;
    onShowToast: (message: string, type: 'success' | 'error' | 'info') => void;
}

const OverviewTab: React.FC<OverviewTabProps> = ({
    todayData,
    weekStats,
    goals,
    studyHistory,
    reviewContents,
    tasks,
    taskGroups,
    subjects,
    onReload,
    onReloadTasks,
    onShowToast
}) => {
    const [showQuickStudyModal, setShowQuickStudyModal] = useState(false);
    const [showManualModal, setShowManualModal] = useState(false);
    const [showGoalModal, setShowGoalModal] = useState(false);
    const [showStudyTypeModal, setShowStudyTypeModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [studyFilter, setStudyFilter] = useState<string>('all');
    const [editingStudy, setEditingStudy] = useState<string | null>(null);
    const [deletingStudy, setDeletingStudy] = useState<string | null>(null);
    const [customTypes, setCustomTypes] = useState<{ name: string, color: string }[]>([]);

    // ========== TASKS STATE ==========
    const [showTaskModal, setShowTaskModal] = useState(false);
    const [editingTask, setEditingTask] = useState<any | null>(null);
    const [showDeleteTaskModal, setShowDeleteTaskModal] = useState(false);
    const [deletingTask, setDeletingTask] = useState<string | null>(null);
    const [showCompleted, setShowCompleted] = useState(false);
    const [quickTaskTitle, setQuickTaskTitle] = useState('');
    const [activeGroupFilter, setActiveGroupFilter] = useState<string | null>(null);
    const [groupByMode, setGroupByMode] = useState(false);
    const [expandedTaskIds, setExpandedTaskIds] = useState<Set<string>>(new Set());
    const quickInputRef = useRef<HTMLInputElement>(null);

    // ✅ Tudo do timer vem do contexto global
    const { activeTimer, startTimer, openFullScreen, openStopModal } = useStudyTimer();

    // ========== CORES PADRONIZADAS POR TIPO ==========
    const STUDY_COLORS: { [key: string]: string } = {
        flashcards: '#2563eb',
        questions: '#9333ea',
        reading: '#ea580c',
        review: '#10b956',
        mindmap: '#0891b2',
        notes: '#ca8a04',
        class: '#e11d48',
        other: '#6B7280'
    };

    const STUDY_LABELS: { [key: string]: string } = {
        flashcards: 'Flashcards',
        questions: 'Questões',
        reading: 'Leitura',
        review: 'Revisão',
        mindmap: 'Mapa Mental',
        notes: 'Resumos e Anotações',
        class: 'Aula',
        other: 'Outro'
    };

    // ========== CARREGAR TIPOS CUSTOMIZADOS ==========
    useEffect(() => {
        const loadCustomTypes = async () => {
            try {
                const token = localStorage.getItem('accessToken');
                const res = await fetch(`${API_BASE_URL}/planner/custom-study-types`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                if (res.ok) {
                    const data = await res.json();
                    setCustomTypes(data.types || []);
                }
            } catch (e) { }
        };
        loadCustomTypes();
    }, [studyHistory]);

    const onReloadRef = React.useRef(onReload);
    useEffect(() => {
        onReloadRef.current = onReload;
    }, [onReload]);

    useEffect(() => {
        const now = new Date();
        const msUntilMidnight =
            new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 0).getTime() - now.getTime();

        const timeout = setTimeout(() => {
            onReloadRef.current(); // recarrega os dados com a nova data
        }, msUntilMidnight);

        return () => clearTimeout(timeout);
    }, []); // roda uma vez ao montar

    // ========== HELPERS DE COR E LABEL ==========
    const getSessionColor = (session: any): string => {
        if (session.type !== 'other') return STUDY_COLORS[session.type] || STUDY_COLORS.other;
        if (session.color) return session.color;
        const found = customTypes.find(c => c.name === session.customType);
        return found ? found.color : STUDY_COLORS.other;
    };

    const getSessionLabel = (session: any): string => {
        if (session.type !== 'other') return session.subject || STUDY_LABELS[session.type];
        return session.customType || 'Outro';
    };

    // ========== TIMER — delega ao contexto ==========
    // ✅ handleStartTimer agora chama startTimer do contexto
    const handleStartTimer = async (type: string, subject?: string, customType?: string, color?: string) => {
        await startTimer(type, subject, customType, color);
        setShowQuickStudyModal(false);
        onReload(); // atualiza o histórico após iniciar
    };

    // ========== HISTÓRICO ==========
    const handleDeleteStudy = async () => {
        if (!deletingStudy) return;
        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch(`${API_BASE_URL}/planner/study-sessions/${deletingStudy}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.ok) {
                onShowToast('Registro excluído', 'info');
                setShowDeleteModal(false);
                setDeletingStudy(null);
                onReload();
            }
        } catch (error) {
            onShowToast('Erro ao excluir', 'error');
        }
    };

    const handleUpdateStudyType = async (sessionId: string, type: string, customType?: string, color?: string) => {
        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch(`${API_BASE_URL}/planner/study-sessions/${sessionId}/type`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ type, customType, color })
            });
            if (response.ok) {
                onShowToast('Tipo atualizado', 'success');
                setEditingStudy(null);
                setShowStudyTypeModal(false);
                onReload();
            }
        } catch (error) {
            onShowToast('Erro ao atualizar', 'error');
        }
    };

    // ========== TAREFAS ==========
    const PRIORITY_CONFIG: Record<string, { color: string; label: string }> = {
        alta:  { color: '#dc2626', label: 'Alta'  },
        media: { color: '#2a4a5a', label: 'Média' },
        baixa: { color: '#9ca3af', label: 'Baixa' }
    };

    const getTodayISO = () => {
        const d = new Date();
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    };

    const getNextWeekISO = () => {
        const d = new Date();
        d.setDate(d.getDate() + 7);
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    };

    const todayISO = getTodayISO();
    const nextWeekISO = getNextWeekISO();

    const priorityOrder: Record<string, number> = { alta: 0, media: 1, baixa: 2 };
    const sortByPriority = (arr: any[]) =>
        [...arr].sort((a, b) => (priorityOrder[a.priority] ?? 1) - (priorityOrder[b.priority] ?? 1));

    const pendingTasks   = tasks.filter(t => !t.isCompleted);
    const completedTasks = tasks.filter(t => t.isCompleted).slice(0, 30);

    const overdueTasks  = sortByPriority(pendingTasks.filter(t => t.dueDate && t.dueDate < todayISO));
    const todayTasks    = sortByPriority(pendingTasks.filter(t => t.dueDate === todayISO));
    const upcomingTasks = sortByPriority(pendingTasks.filter(t => t.dueDate && t.dueDate > todayISO && t.dueDate <= nextWeekISO));
    const noDueTasks    = sortByPriority(pendingTasks.filter(t => !t.dueDate));

    const getGroupColor = (groupName: string | null): string => {
        if (!groupName) return 'var(--plnr-text-secondary)';
        return taskGroups.find(g => g.name === groupName)?.color || '#6b7280';
    };

    const filterByGroup = (arr: any[]) =>
        activeGroupFilter ? arr.filter(t => t.group === activeGroupFilter) : arr;

    // Grupos que aparecem em tarefas pendentes (na ordem da paleta)
    const groupsWithTasks = taskGroups.filter(g =>
        tasks.some(t => !t.isCompleted && t.group === g.name)
    );

    const taskSections = [
        { key: 'overdue',  icon: <AlertCircle  size={14} style={{ color: '#dc2626' }} />,                    label: 'Atrasadas', tasks: filterByGroup(overdueTasks),  isOverdue: true  },
        { key: 'today',    icon: <CalendarCheck size={14} style={{ color: 'var(--plnr-primary)' }} />,       label: 'Hoje',      tasks: filterByGroup(todayTasks),    isOverdue: false },
        { key: 'upcoming', icon: <CalendarDays  size={14} style={{ color: '#2a4a5a' }} />,                   label: 'Próximas',  tasks: filterByGroup(upcomingTasks), isOverdue: false },
        { key: 'nodue',    icon: <Inbox         size={14} style={{ color: 'var(--plnr-text-secondary)' }} />, label: 'Sem prazo', tasks: filterByGroup(noDueTasks),    isOverdue: false },
    ];

    const toggleExpand = (taskId: string) => {
        setExpandedTaskIds(prev => {
            const next = new Set(prev);
            next.has(taskId) ? next.delete(taskId) : next.add(taskId);
            return next;
        });
    };

    // Opções de matéria: StudySubject docs + nomes únicos do histórico de sessões
    const subjectOptions: string[] = [...new Set([
        ...subjects.map((s: any) => s.name),
        ...studyHistory.filter(s => s.subject?.trim()).map(s => s.subject.trim())
    ])].sort();

    const formatTaskDate = (dateStr: string) => {
        const [year, month, day] = dateStr.split('-').map(Number);
        return new Date(year, month - 1, day).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
    };

    const handleQuickAddTask = async () => {
        if (!quickTaskTitle.trim()) return;
        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch(`${API_BASE_URL}/planner/tasks`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ title: quickTaskTitle.trim(), priority: 'media' })
            });
            if (response.ok) {
                setQuickTaskTitle('');
                onReloadTasks();
            }
        } catch { }
    };

    const handleToggleTask = async (task: any) => {
        try {
            const token = localStorage.getItem('accessToken');
            await fetch(`${API_BASE_URL}/planner/tasks/${task._id}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ isCompleted: !task.isCompleted })
            });
            onReloadTasks();
        } catch { }
    };

    const handleDeleteTask = async () => {
        if (!deletingTask) return;
        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch(`${API_BASE_URL}/planner/tasks/${deletingTask}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.ok) {
                onShowToast('Tarefa excluída', 'info');
                setShowDeleteTaskModal(false);
                setDeletingTask(null);
                onReloadTasks();
            }
        } catch {
            onShowToast('Erro ao excluir tarefa', 'error');
        }
    };

    // ========== METAS ==========
    const handleCompleteGoal = async (goalId: string) => {
        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch(`${API_BASE_URL}/planner/goals/${goalId}/complete`, {
                method: 'PATCH',
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.ok) {
                onShowToast('Meta concluída', 'success');
                onReload();
            }
        } catch (error) {
            onShowToast('Erro ao completar meta', 'error');
        }
    };

    // ========== HELPERS ==========
    const formatTime = (minutes: number): string => {
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        if (hours > 0) return `${hours}h${mins > 0 ? ` ${mins}min` : ''}`;
        return `${mins}min`;
    };

    const formatTimerDisplay = (seconds: number): string => {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const secs = seconds % 60;
        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    const getUpcomingReviews = () => {
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);
        const dayAfterTomorrow = new Date(tomorrow);
        dayAfterTomorrow.setDate(dayAfterTomorrow.getDate() + 1);

        const todayAndTomorrow = reviewContents
            .filter(r => {
                if (!r.nextReview) return false;
                return new Date(r.nextReview) < dayAfterTomorrow;
            })
            .sort((a, b) => new Date(a.nextReview).getTime() - new Date(b.nextReview).getTime())
            .slice(0, 5);

        if (todayAndTomorrow.length === 0) {
            return reviewContents
                .filter(r => r.nextReview)
                .sort((a, b) => new Date(a.nextReview).getTime() - new Date(b.nextReview).getTime())
                .slice(0, 1);
        }
        return todayAndTomorrow;
    };

    const upcomingReviews = getUpcomingReviews();

    // ========== RENDER HELPERS (TAREFAS) ==========
    const renderTaskItem = (task: any, isOverdue = false) => {
        const pc = PRIORITY_CONFIG[task.priority] || PRIORITY_CONFIG.media;
        const groupColor = getGroupColor(task.group);
        const isExpanded = expandedTaskIds.has(task._id);
        const hasNotes = !!task.notes?.trim();

        return (
            <div
                key={task._id}
                className="plnr-ov-task-item"
                style={task.group ? { borderLeft: `3px solid ${groupColor}` } : undefined}
            >
                <div className="plnr-ov-task-item-row">
                    <button
                        className="plnr-ov-task-checkbox"
                        style={{ borderColor: pc.color }}
                        onClick={() => handleToggleTask(task)}
                        title="Concluir tarefa"
                    />
                    <div className="plnr-ov-task-priority-dot" style={{ backgroundColor: pc.color }} title={pc.label} />
                    <div
                        className="plnr-ov-task-info"
                        onClick={hasNotes ? () => toggleExpand(task._id) : undefined}
                        style={hasNotes ? { cursor: 'pointer' } : undefined}
                    >
                        <div className="plnr-ov-task-title-row">
                            <span className="plnr-ov-task-title">{task.title}</span>
                            {hasNotes && (
                                <ChevronRight
                                    size={13}
                                    className={`plnr-ov-task-expand-icon${isExpanded ? ' open' : ''}`}
                                />
                            )}
                        </div>
                        <div className="plnr-ov-task-meta">
                            {task.group && (
                                <span
                                    className="plnr-ov-task-group-badge"
                                    style={{ color: groupColor, backgroundColor: `${groupColor}1a`, borderColor: `${groupColor}44` }}
                                >
                                    {task.group}
                                </span>
                            )}
                            {task.subject && <span className="plnr-ov-task-badge">{task.subject}</span>}
                            {task.dueDate && (
                                <span className={`plnr-ov-task-date${isOverdue ? ' overdue' : ''}`}>
                                    {formatTaskDate(task.dueDate)}
                                </span>
                            )}
                        </div>
                    </div>
                    <div className="plnr-ov-task-actions">
                        <button className="plnr-ov-btn-icon" onClick={() => { setEditingTask(task); setShowTaskModal(true); }} title="Editar">
                            <Edit2 size={14} />
                        </button>
                        <button className="plnr-ov-btn-icon delete" onClick={() => { setDeletingTask(task._id); setShowDeleteTaskModal(true); }} title="Excluir">
                            <Trash2 size={14} />
                        </button>
                    </div>
                </div>
                {hasNotes && isExpanded && (
                    <div className="plnr-ov-task-notes">{task.notes}</div>
                )}
            </div>
        );
    };

    const renderGroupedTasks = (sectionTasks: any[], isOverdue: boolean) => {
        const grouped: Record<string, any[]> = {};
        sectionTasks.forEach(t => {
            const key = t.group || '__none__';
            if (!grouped[key]) grouped[key] = [];
            grouped[key].push(t);
        });
        return Object.entries(grouped).map(([groupKey, groupTasks]) => (
            <div key={groupKey}>
                <div className="plnr-ov-task-subgroup-header">
                    <Folder size={12} style={{ color: groupKey !== '__none__' ? getGroupColor(groupKey) : 'var(--plnr-text-secondary)', flexShrink: 0 }} />
                    <span style={{ color: groupKey !== '__none__' ? getGroupColor(groupKey) : undefined }}>
                        {groupKey === '__none__' ? 'Sem grupo' : groupKey}
                    </span>
                </div>
                {groupTasks.map(task => renderTaskItem(task, isOverdue))}
            </div>
        ));
    };

    // ========== FILTRAR HISTÓRICO ==========
    const filteredHistory = studyFilter === 'all'
        ? studyHistory
        : studyFilter === 'other'
            ? studyHistory.filter(s => s.type === 'other')
            : studyHistory.filter(s => s.type === studyFilter);

    // ========== GRÁFICO ==========
    const timeByType = filteredHistory.reduce((acc: any, session: any) => {
        const key = session.type === 'other'
            ? `other__${session.customType || 'Outro'}`
            : session.type;

        if (!acc[key]) {
            acc[key] = {
                minutes: 0,
                color: getSessionColor(session),
                label: session.type === 'other'
                    ? (session.customType || 'Outro')
                    : STUDY_LABELS[session.type] || session.type
            };
        }
        acc[key].minutes += session.duration || 0;
        return acc;
    }, {});

    const totalTime = Object.values(timeByType).reduce((sum: number, val: any) => sum + val.minutes, 0) as number;

    return (
        <div className="plnr-ov-content">
            {/* ========== HOJE + TIMER | REVISÕES ========== */}
            <div className="plnr-ov-top-layout">
                <div className="plnr-ov-left-column">
                    <div className="plnr-ov-today-card">
                        <h2>
                            {todayData && (() => {
                                // "2025-02-28" → split para evitar conversão de timezone
                                const [year, month, day] = todayData.date.split('-').map(Number);
                                return new Date(year, month - 1, day).toLocaleDateString('pt-BR', {
                                    weekday: 'long', day: 'numeric', month: 'long'
                                });
                            })()}
                        </h2>
                        <div className="plnr-ov-today-stats">
                            <div className="plnr-ov-stat">
                                <Clock size={18} />
                                <span>{formatTime(todayData?.totalMinutesToday || 0)}</span>
                            </div>
                            <div className="plnr-ov-stat">
                                <Target size={18} />
                                <span>{todayData?.todaySessions?.length || 0} sessões</span>
                            </div>
                        </div>
                    </div>

                    <div className="plnr-ov-timer-card">
                        {!activeTimer ? (
                            <div className="plnr-ov-timer-idle">
                                <div className="plnr-ov-timer-display-idle">00:00:00</div>
                                {/* ✅ Sem timer ativo: só botão play */}
                                <div className="plnr-ov-timer-idle-actions">
                                    <button
                                        className="plnr-ov-timer-play-btn"
                                        onClick={() => setShowQuickStudyModal(true)}
                                    >
                                        <Play size={20} />
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div className="plnr-ov-timer-running">
                                <div className="plnr-ov-timer-display-running">
                                    {formatTimerDisplay(activeTimer.elapsed)}
                                </div>
                                {/* ✅ Com timer ativo: fullscreen + parar */}
                                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                    <button
                                        className="plnr-ov-timer-fullscreen-btn"
                                        onClick={openFullScreen}
                                        title="Tela cheia"
                                    >
                                        <Maximize2 size={18} />
                                    </button>
                                    <button
                                        className="plnr-ov-timer-pause-btn"
                                        onClick={openStopModal}
                                    >
                                        <Pause size={18} />
                                        Parar
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <div className="plnr-ov-reviews-card">
                    <h3>Próximas Revisões</h3>
                    {upcomingReviews.length > 0 ? (
                        <div className="plnr-ov-reviews-list">
                            {upcomingReviews.map(review => (
                                <div key={review._id} className="plnr-ov-review-item">
                                    <span className="plnr-ov-review-content">{review.content}</span>
                                    <span className="plnr-ov-review-date">
                                        {new Date(review.nextReview).toLocaleDateString('pt-BR', {
                                            day: '2-digit', month: 'short'
                                        })}
                                    </span>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="plnr-ov-empty">Nenhuma revisão agendada</p>
                    )}
                </div>
            </div>

            {/* ========== HISTÓRICO + GRÁFICO ========== */}
            <div className="plnr-ov-history-section">
                <div className="plnr-ov-history-header">
                    <h3>Histórico de Estudo</h3>
                    <div className="plnr-ov-history-controls">
                        <button className="plnr-ov-btn-manual" onClick={() => setShowManualModal(true)}>
                            <Plus size={16} />
                            Registrar Manualmente
                        </button>
                        <div className="plnr-ov-history-filter">
                            <Filter size={16} />
                            <select
                                value={studyFilter}
                                onChange={(e) => setStudyFilter(e.target.value)}
                                className="plnr-ov-filter-select"
                            >
                                <option value="all">Todos</option>
                                <option value="flashcards">Flashcards</option>
                                <option value="questions">Questões</option>
                                <option value="reading">Leitura</option>
                                <option value="review">Revisão</option>
                                <option value="mindmap">Mapa Mental</option>
                                <option value="notes">Resumos e Anotações</option>
                                <option value="class">Aula</option>
                                <option value="other">Outro</option>
                            </select>
                        </div>
                    </div>
                </div>

                <div className="plnr-ov-history-content">
                    <div className="plnr-ov-history-list">
                        {filteredHistory.length > 0 ? (
                            filteredHistory.slice(0, 10).map(session => (
                                <div key={session._id} className="plnr-ov-history-item">
                                    <div
                                        className="plnr-ov-history-color"
                                        style={{ backgroundColor: getSessionColor(session) }}
                                    />
                                    <div className="plnr-ov-history-info">
                                        <h4>{getSessionLabel(session)}</h4>
                                        <p>
                                            {new Date(session.startTime).toLocaleDateString('pt-BR', {
                                                day: '2-digit', month: '2-digit', year: '2-digit'
                                            })} • {formatTime(session.duration)} • {new Date(session.startTime).toLocaleTimeString('pt-BR', {
                                                hour: '2-digit', minute: '2-digit'
                                            })}
                                        </p>
                                    </div>
                                    <div className="plnr-ov-history-actions">
                                        <button
                                            className="plnr-ov-btn-icon"
                                            onClick={() => {
                                                setEditingStudy(session._id);
                                                setShowStudyTypeModal(true);
                                            }}
                                        >
                                            <Edit2 size={14} />
                                        </button>
                                        <button
                                            className="plnr-ov-btn-icon delete"
                                            onClick={() => {
                                                setDeletingStudy(session._id);
                                                setShowDeleteModal(true);
                                            }}
                                        >
                                            <Trash2 size={14} />
                                        </button>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <p className="plnr-ov-empty">Nenhum registro</p>
                        )}
                    </div>

                    <div className="plnr-ov-chart">
                        <h4>Distribuição de Tempo</h4>
                        <div className="plnr-ov-chart-bars">
                            {Object.entries(timeByType).map(([key, data]: any) => {
                                const height = totalTime > 0 ? (data.minutes / totalTime) * 100 : 0;
                                return (
                                    <div key={key} className="plnr-ov-chart-bar-wrapper">
                                        <div
                                            className="plnr-ov-chart-bar"
                                            style={{ height: `${height}%`, backgroundColor: data.color }}
                                        />
                                        <span className="plnr-ov-chart-value">{formatTime(data.minutes)}</span>
                                        <span className="plnr-ov-chart-label">{data.label}</span>
                                    </div>
                                );
                            })}
                            {Object.keys(timeByType).length === 0 && (
                                <p className="plnr-ov-empty">Sem dados</p>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* ========== METAS ========== */}
            <div className="plnr-ov-goals-section">
                <div className="plnr-ov-section-header">
                    <h3>Metas</h3>
                    <button className="plnr-ov-btn-add" onClick={() => setShowGoalModal(true)}>
                        <Plus size={16} />
                        Nova Meta
                    </button>
                </div>
                <div className="plnr-ov-goals-grid">
                    {goals.filter(g => !g.isCompleted).length > 0 ? (
                        goals.filter(g => !g.isCompleted).map(goal => (
                            <div key={goal._id} className="plnr-ov-goal-card">
                                <div className="plnr-ov-goal-header">
                                    <div className="plnr-ov-goal-color" style={{ backgroundColor: goal.color || 'var(--platform-accent-light)' }} />
                                    <h4>{goal.title}</h4>
                                </div>
                                {goal.description && <p className="plnr-ov-goal-desc">{goal.description}</p>}
                                <button className="plnr-ov-goal-complete" onClick={() => handleCompleteGoal(goal._id)}>
                                    Concluir
                                </button>
                            </div>
                        ))
                    ) : (
                        <p className="plnr-ov-empty">Nenhuma meta ativa</p>
                    )}
                </div>
            </div>

            {/* ========== TAREFAS ========== */}
            <div className="plnr-ov-tasks-section">
                <div className="plnr-ov-section-header">
                    <h3>Tarefas</h3>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <button
                            className={`plnr-ov-task-group-toggle${groupByMode ? ' active' : ''}`}
                            onClick={() => setGroupByMode(v => !v)}
                            title="Agrupar por grupo"
                        >
                            <Layers size={14} />
                            Agrupar
                        </button>
                        <button className="plnr-ov-btn-add" onClick={() => { setEditingTask(null); setShowTaskModal(true); }}>
                            <Plus size={16} />
                            Nova Tarefa
                        </button>
                    </div>
                </div>

                {/* Barra de filtro por grupo */}
                {groupsWithTasks.length > 0 && (
                    <div className="plnr-ov-task-filters">
                        <button
                            className={`plnr-ov-task-filter-pill${activeGroupFilter === null ? ' active' : ''}`}
                            onClick={() => setActiveGroupFilter(null)}
                        >
                            Todos
                        </button>
                        {groupsWithTasks.map(g => (
                            <button
                                key={g._id}
                                className={`plnr-ov-task-filter-pill${activeGroupFilter === g.name ? ' active' : ''}`}
                                onClick={() => setActiveGroupFilter(activeGroupFilter === g.name ? null : g.name)}
                                style={activeGroupFilter === g.name
                                    ? { backgroundColor: g.color, borderColor: g.color, color: '#fff' }
                                    : { borderColor: `${g.color}88`, color: g.color }
                                }
                            >
                                <span className="plnr-ov-task-filter-dot" style={{ backgroundColor: g.color }} />
                                {g.name}
                            </button>
                        ))}
                    </div>
                )}

                {/* Quick add */}
                <div className="plnr-ov-task-quickadd">
                    <input
                        ref={quickInputRef}
                        type="text"
                        value={quickTaskTitle}
                        onChange={(e) => setQuickTaskTitle(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleQuickAddTask()}
                        placeholder="Adicionar tarefa rápida... (Enter para salvar)"
                        className="plnr-ov-task-quickadd-input"
                    />
                    <button className="plnr-ov-task-quickadd-btn" onClick={handleQuickAddTask}>
                        <Plus size={16} />
                    </button>
                </div>

                {/* Seções pendentes */}
                {taskSections.map(section =>
                    section.tasks.length > 0 && (
                        <div key={section.key} className="plnr-ov-task-group">
                            <div className="plnr-ov-task-group-header">
                                {section.icon}
                                <span className="plnr-ov-task-group-label">{section.label}</span>
                                <span className="plnr-ov-task-group-count">{section.tasks.length}</span>
                            </div>
                            {groupByMode
                                ? renderGroupedTasks(section.tasks, section.isOverdue)
                                : section.tasks.map(task => renderTaskItem(task, section.isOverdue))
                            }
                        </div>
                    )
                )}

                {/* Concluídas (colapsável) */}
                {filterByGroup(completedTasks).length > 0 && (
                    <div className="plnr-ov-task-group">
                        <button
                            className="plnr-ov-task-group-header clickable"
                            onClick={() => setShowCompleted(v => !v)}
                        >
                            <CheckCircle2 size={14} style={{ color: 'var(--plnr-success)' }} />
                            <span className="plnr-ov-task-group-label">Concluídas</span>
                            <span className="plnr-ov-task-group-count">{filterByGroup(completedTasks).length}</span>
                            <ChevronDown
                                size={14}
                                className={`plnr-ov-task-chevron${showCompleted ? ' open' : ''}`}
                            />
                        </button>
                        {showCompleted && filterByGroup(completedTasks).map(task => {
                            const pc = PRIORITY_CONFIG[task.priority] || PRIORITY_CONFIG.media;
                            const groupColor = getGroupColor(task.group);
                            return (
                                <div
                                    key={task._id}
                                    className="plnr-ov-task-item completed"
                                    style={task.group ? { borderLeft: `3px solid ${groupColor}` } : undefined}
                                >
                                    <div className="plnr-ov-task-item-row">
                                        <button
                                            className="plnr-ov-task-checkbox checked"
                                            style={{ backgroundColor: pc.color, borderColor: pc.color }}
                                            onClick={() => handleToggleTask(task)}
                                            title="Desmarcar"
                                        >
                                            <Check size={11} />
                                        </button>
                                        <div className="plnr-ov-task-priority-dot" style={{ backgroundColor: pc.color }} />
                                        <div className="plnr-ov-task-info">
                                            <span className="plnr-ov-task-title">{task.title}</span>
                                            <div className="plnr-ov-task-meta">
                                                {task.group && (
                                                    <span
                                                        className="plnr-ov-task-group-badge"
                                                        style={{ color: groupColor, backgroundColor: `${groupColor}1a`, borderColor: `${groupColor}44` }}
                                                    >
                                                        {task.group}
                                                    </span>
                                                )}
                                                {task.subject && <span className="plnr-ov-task-badge">{task.subject}</span>}
                                            </div>
                                        </div>
                                        <div className="plnr-ov-task-actions">
                                            <button
                                                className="plnr-ov-btn-icon delete"
                                                onClick={() => { setDeletingTask(task._id); setShowDeleteTaskModal(true); }}
                                                title="Excluir"
                                            >
                                                <Trash2 size={14} />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {tasks.length === 0 && (
                    <p className="plnr-ov-empty">Nenhuma tarefa cadastrada</p>
                )}
            </div>

            {/* ========== MODAIS ========== */}
            {showQuickStudyModal && (
                <QuickStudyModal
                    onClose={() => setShowQuickStudyModal(false)}
                    onStart={handleStartTimer}
                />
            )}

            {showManualModal && (
                <ManualSessionModal
                    onClose={() => setShowManualModal(false)}
                    onSave={() => { setShowManualModal(false); onReload(); }}
                    onShowToast={onShowToast}
                />
            )}

            {showGoalModal && (
                <GoalModal
                    onClose={() => setShowGoalModal(false)}
                    onSave={() => { setShowGoalModal(false); onReload(); }}
                    onShowToast={onShowToast}
                />
            )}

            {showStudyTypeModal && editingStudy && (
                <StudyTypeModal
                    sessionId={editingStudy}
                    currentType={studyHistory.find(s => s._id === editingStudy)?.type || 'flashcards'}
                    currentCustomType={studyHistory.find(s => s._id === editingStudy)?.customType}
                    currentColor={studyHistory.find(s => s._id === editingStudy)?.color}
                    onClose={() => { setShowStudyTypeModal(false); setEditingStudy(null); }}
                    onUpdate={handleUpdateStudyType}
                />
            )}

            {showTaskModal && (
                <TaskModal
                    task={editingTask}
                    subjectOptions={subjectOptions}
                    taskGroups={taskGroups}
                    onClose={() => { setShowTaskModal(false); setEditingTask(null); }}
                    onSave={() => { setShowTaskModal(false); setEditingTask(null); onReloadTasks(); }}
                    onShowToast={onShowToast}
                />
            )}

            {showDeleteTaskModal && (
                <div className="plnr-modal-overlay" onClick={() => setShowDeleteTaskModal(false)}>
                    <div className="plnr-modal plnr-modal-confirm" onClick={(e) => e.stopPropagation()}>
                        <div className="plnr-modal-header">
                            <h3>Confirmar Exclusão</h3>
                        </div>
                        <div className="plnr-modal-body">
                            <p>Tem certeza que deseja excluir esta tarefa?</p>
                        </div>
                        <div className="plnr-modal-footer">
                            <button className="plnr-btn secondary" onClick={() => { setShowDeleteTaskModal(false); setDeletingTask(null); }}>
                                Cancelar
                            </button>
                            <button className="plnr-btn danger" onClick={handleDeleteTask}>
                                Excluir
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showDeleteModal && (
                <div className="plnr-modal-overlay" onClick={() => setShowDeleteModal(false)}>
                    <div className="plnr-modal plnr-modal-confirm" onClick={(e) => e.stopPropagation()}>
                        <div className="plnr-modal-header">
                            <h3>Confirmar Exclusão</h3>
                        </div>
                        <div className="plnr-modal-body">
                            <p>Tem certeza que deseja excluir este registro de estudo?</p>
                        </div>
                        <div className="plnr-modal-footer">
                            <button className="plnr-btn secondary" onClick={() => { setShowDeleteModal(false); setDeletingStudy(null); }}>
                                Cancelar
                            </button>
                            <button className="plnr-btn danger" onClick={handleDeleteStudy}>
                                Excluir
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default OverviewTab;